import { Box, TextField, Typography } from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { FlexBox } from 'components/Containers/FlexBox';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { Selector } from 'components/Form/Selectors/Selector';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import ExternalUserHandler from 'handlers/external.user.handler';
import { useEffect, useMemo, useState } from 'react';
import { modalInputFieldStyle } from 'styles/global.css';
import { type ICreateExternalUserPayload } from 'types/payloads/user.payload.types';
import { USER_PERMISSION_LEVEL_OPTIONS } from 'utils/enums';
import { createOrUpdateExternalUserRequestBodySchema } from 'utils/schemas/user.schema';
import { validateYupSchema } from 'utils/validators/schema.validator';
import { type RoleType } from 'types/user.types';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { ThemePalette } from 'mui.theme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { MultiSelector } from 'components/Form/Selectors/MultiSelector';
import {
  handleSelectCustomerAll,
  handleSelectCustomerSingle,
} from '../../helpers/common';
import { useAppSelector } from 'store/hook';
import { useCustomerList } from 'hooks/useCustomerList';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  /**
   * Title of the Modal
   */
  title: string;
  /**
   * Boolean Value to determine if modal is displpayed or not
   */
  open: boolean;
  /**
   * User Data modal is to render
   */
  userData: RoleType;
  /**
   * Function to call to close modal
   */
  handleAbort: () => void;
  enableDarkTheme: boolean;
}

export const UpdateExternalUserModal = ({
  title,
  open,
  handleAbort,
  userData,
  enableDarkTheme,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const userHandler = new ExternalUserHandler();

  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const role = useAppSelector((state) => state.authReducer.role);

  const { customerList } = useCustomerList();

  const [isValidForm, setFormValidity] = useState(false);
  const [externalUser, setExternalUser] = useState<ICreateExternalUserPayload>({
    firstName: userData.firstName,
    lastName: userData.lastName,
    email: userData.email,
    customerAccessLevel:
      userData.role !== undefined ? userData.role : 'external-user',
    hierarchyNode: userData.hierarchyNode,
    phoneNumber: userData.phoneNumber,
    userCustomerAssociations: userData.userCustomerAssociations,
  });

  // Validate form as user fills out fields
  useEffect(() => {
    const validateForm = async () =>
      await validateYupSchema(
        externalUser,
        createOrUpdateExternalUserRequestBodySchema
      );

    void validateForm().then((isValid) => {
      setFormValidity(isValid);
    });
  }, [externalUser]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setExternalUser({
      ...externalUser,
      [name]: value,
    });
  };

  const handleSuccess = async () => {
    await userHandler.updateExternalUser(userData.userId, externalUser);
    await userHandler.getAll();
    handleAbort();
  };

  const handlePhoneChange = (value: string) => {
    setExternalUser({
      ...externalUser,
      phoneNumber: value,
    });
  };

  const permissionLvlOptions = useMemo(() => {
    const temp = [...USER_PERMISSION_LEVEL_OPTIONS];
    if (role?.includes('director')) {
      temp.push({
        id: 'external-superadmin',
        display: 'Super Admin',
      });
    }
    return temp;
  }, [USER_PERMISSION_LEVEL_OPTIONS, role]);

  return (
    <InteractiveModal
      initialState={open}
      SuccessButtonProps={{
        disabled: !isValidForm,
        onClick: handleSuccess,
        label: 'EDIT USER',
      }}
      handleAbort={handleAbort}
      title={title}
      confirmCancellation
      enableDarkTheme={enableDarkTheme}
    >
      {/* First Name TextField */}
      <SWTextField
        key="first-name"
        name="firstName"
        label={t('first_name')}
        TextFieldProps={{
          name: 'firstName',
          value: externalUser.firstName,
          onChange: handleChange,
          InputProps: {
            sx: { ...modalInputFieldStyle, height: '37px' },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Last Name TextField */}
      <SWTextField
        key="last-name"
        name="lastName"
        label={t('last_name')}
        TextFieldProps={{
          name: 'lastName',
          value: externalUser.lastName,
          onChange: handleChange,
          InputProps: {
            sx: { ...modalInputFieldStyle, height: '37px' },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Email  TextField */}
      <Box key="email">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('email_address')}
        </Typography>
        <FlexBox>
          <TextField
            name="email"
            onChange={handleChange}
            value={externalUser.email}
            InputProps={{
              sx: { ...modalInputFieldStyle, height: '37px' },
            }}
          />
        </FlexBox>
      </Box>

      {/* Hierarchy Node selector drop down single */}
      <Box key="hierarchyNode">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('hierarchy_node')}
        </Typography>
        <Selector
          value={externalUser.hierarchyNode}
          onChange={(event) => {
            setExternalUser({
              ...externalUser,
              hierarchyNode:
                event.target.value.toString() as ICreateExternalUserPayload['hierarchyNode'],
            });
          }}
          selectorOptions={[
            {
              id: userData.hierarchyNode ?? '',
              display: userData.hierarchyNode ?? '',
            },
          ]}
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      {/** Phone number box */}
      <Box key="phone-number">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('phone_number_2')}
        </Typography>
        <PhoneInput
          country={'ca'}
          value={externalUser.phoneNumber}
          onChange={handlePhoneChange}
          inputStyle={{ width: '270px', height: '20px', minWidth: '270px' }}
          enableSearch={true}
          enableAreaCodes={true}
          buttonStyle={{
            border: 'none',
            backgroundColor: 'transparent',
            padding: 0,
          }}
          placeholder=""
        />
      </Box>
      {/* Permission selector drop down single */}
      <Box key="customerAccessLevel">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('permission_level')}
        </Typography>
        <Selector
          value={externalUser.customerAccessLevel}
          onChange={(event) => {
            const value =
              event.target.value.toString() as ICreateExternalUserPayload['customerAccessLevel'];

            if (value === 'external-admin' || value === 'external-user') {
              setExternalUser((prev) => {
                const { userCustomerAssociations, ...restUserData } = prev;
                return {
                  ...restUserData,
                  customerAccessLevel: value,
                };
              });
            } else {
              setExternalUser((prev) => ({
                ...prev,
                customerAccessLevel: value,
              }));
            }
          }}
          selectorOptions={permissionLvlOptions}
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      {/* Customer account multi selector */}
      {externalUser.customerAccessLevel === 'external-superadmin' ? (
        <Box key="customer-accounts">
          <Typography
            variant="bodyBold"
            display="block"
            mb={'4px'}
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('associated_customer_accounts')}
          </Typography>
          <MultiSelector
            value={externalUser?.userCustomerAssociations ?? []}
            renderValue={(selected) => {
              return `${selected?.length || 0} Customer(s) Selected`;
            }}
            handleSelectOne={(event) => {
              handleSelectCustomerSingle(event, setExternalUser);
            }}
            handleSelectAll={() => {
              handleSelectCustomerAll(customerList, setExternalUser);
            }}
            selectorOptions={customerList}
            enableDarkTheme={enableDarkTheme}
          />
        </Box>
      ) : (
        <></>
      )}

      <FlexBox></FlexBox>

      {/* Non updateable customer data starts here */}
      <Box key="user-id" mt="40px">
        <Typography
          variant="bodyBold"
          display="block"
          mb="4px"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('user_id')}
        </Typography>
        <Typography
          variant="small"
          display="block"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {userData.userId}
        </Typography>
      </Box>
      <Box key="login-count" mt="40px">
        <Typography
          variant="bodyBold"
          display="block"
          mb="4px"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('number_of_logins')}
        </Typography>
        <Typography
          variant="small"
          display="block"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {userData.loginCount}
        </Typography>
      </Box>
      <Box key="last-login" mt="40px">
        <Typography
          variant="bodyBold"
          display="block"
          mb="4px"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('last_login')}
        </Typography>
        <Typography
          variant="small"
          display="block"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {userData.lastLogin
            ? convertUtcToDisplayTime(
                userData.lastLogin,
                preferredTimeDisplayMode,
                timezone
              )
            : null}
        </Typography>
      </Box>
    </InteractiveModal>
  );
};
